import { Fragment, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { PREFERENCIAS_VERSION_POR_DEFECTO, bitrateMbps, valorarVersiones } from '@film/shared';
import type { Valoracion } from '@film/shared';
import { api } from '../lib/api.js';
import type { ApiFallo, Ficha as FichaVista, ReleaseVista, ResultadoLote } from '../lib/api.js';
import { InsigniaBiblioteca } from '../components/InsigniaBiblioteca.js';
import { CeldaIdoneidad } from '../components/CeldaIdoneidad.js';
import { TemporadasDeVersion, type EncolandoSerie } from '../components/TemporadasDeVersion.js';
import { formatearTamano } from '../lib/eventos.js';

const ESCALA = ['480p', '576p', '720p', '1080p', '2160p'];

interface Valorada {
  version: ReleaseVista;
  valoracion: Valoracion;
}

/** Orden por IDONEIDAD: arriba lo que cumple lo que se pidió en Ajustes.
 *
 *  A igualdad de puntos se desempata por RESOLUCIÓN y luego por descargas —el mejor
 *  indicador de salud que publica el sitio—, nunca por tamaño en bruto: en Scarface el
 *  2160p ocupa 4,14 GB y el 1080p 6,97 GB, así que el peso pondría la peor arriba. El
 *  tamaño sí pesa dentro de los puntos, pero comparado con las versiones de su misma
 *  resolución, que es otra cosa. */
function ordenar(valoradas: Valorada[]): Valorada[] {
  return [...valoradas].sort((a, b) => {
    if (b.valoracion.puntos !== a.valoracion.puntos) return b.valoracion.puntos - a.valoracion.puntos;
    const ia = a.version.resolution ? ESCALA.indexOf(a.version.resolution) : -1;
    const ib = b.version.resolution ? ESCALA.indexOf(b.version.resolution) : -1;
    if (ia !== ib) return ib - ia;
    return (b.version.downloadCount ?? 0) - (a.version.downloadCount ?? 0);
  });
}

/** «26 episodios · T1–T3», que es lo que se quiere saber de una versión de una serie antes de
 *  desplegarla: cuánto trae y de qué temporadas. Los sin temporada se cuentan aparte. */
function describirSerie(r: ReleaseVista): string {
  const videos = r.files.filter((f) => f.kind === 'video');
  const temporadas = [...new Set(videos.map((f) => f.season).filter((t): t is number => t != null))].sort((a, b) => a - b);
  const sinTemporada = videos.filter((f) => f.season == null).length;
  const rango =
    temporadas.length === 0 ? null
      : temporadas.length === 1 ? `T${temporadas[0]}`
        : `T${temporadas[0]}–T${temporadas[temporadas.length - 1]}`;
  return [
    `${videos.length} ${videos.length === 1 ? 'episodio' : 'episodios'}`,
    rango,
    sinTemporada ? `${sinTemporada} sin numerar` : null,
  ].filter(Boolean).join(' · ');
}

/** Lo que pasó al pedir una temporada, en una frase: cuántas entraron y por qué no las demás. */
function resumirLote(r: ResultadoLote): { tipo: 'success' | 'info' | 'warning'; texto: string } {
  const yaEstaban = r.omitidas.filter((o) => o.motivo === 'ya_pedida').length;
  const enPlex = r.omitidas.filter((o) => o.motivo === 'en_plex').length;
  const rechazadas = r.omitidas.filter((o) => o.motivo === 'rechazada');
  const partes = [
    r.descargas.length ? `${r.descargas.length} ${r.descargas.length === 1 ? 'episodio encolado' : 'episodios encolados'}` : null,
    enPlex ? `${enPlex} ya en Plex` : null,
    yaEstaban ? `${yaEstaban} ya ${yaEstaban === 1 ? 'estaba' : 'estaban'} en descargas` : null,
    rechazadas.length
      ? `aMule rechazó ${rechazadas.length}: ${rechazadas.map((o) => o.detalle ?? o.fileName).join('; ')}`
      : null,
  ].filter(Boolean);
  if (rechazadas.length) return { tipo: 'warning', texto: partes.join(' · ') };
  if (!r.descargas.length) {
    return {
      tipo: 'info',
      texto: enPlex && !yaEstaban
        ? 'No había nada que encolar: toda la temporada está ya en Plex.'
        : 'No había nada que encolar: toda la temporada estaba ya en Plex o en descargas.',
    };
  }
  return { tipo: 'success', texto: partes.join(' · ') };
}

function describirFicheros(r: ReleaseVista): string | null {
  if (r.files.length <= 1) return null;
  const videos = r.files.filter((f) => f.kind === 'video').length;
  const subs = r.files.filter((f) => f.kind === 'subtitles').length;
  const partes = videos > 1 ? `${videos} partes` : `${videos} vídeo${videos === 1 ? '' : 's'}`;
  return [`${r.files.length} ficheros`, partes, subs ? `${subs} subtítulo${subs === 1 ? '' : 's'}` : null]
    .filter(Boolean)
    .join(' · ');
}

/** Un error tiene que decir qué hacer, no solo que algo salió mal. */
function explicarFallo(f: ApiFallo): string {
  switch (f.code) {
    case 'already_queued':
      return 'Eso ya está en la cola de descargas.';
    case 'amule_unreachable':
    case 'amule_unconfigured':
      return 'aMule no está funcionando, así que no se puede encolar nada. Levanta su contenedor y vuelve a intentarlo.';
    case 'amule_degraded':
    case 'amule_ec_unavailable':
      return 'aMule responde pero su demonio interno no está conectado. Revisa los logs del contenedor.';
    case 'indexer_bad_credentials':
      return 'HispaShare rechazó las credenciales. Revisa HISPASHARE_USER y HISPASHARE_PASSWORD en el .env.';
    case 'link_unresolved':
      return 'HispaShare no devolvió el enlace de descarga. Puede ser algo puntual: inténtalo otra vez.';
    default:
      return f.message;
  }
}

/** La página de la ficha: la ruta trae el id y el resto es `FichaContenido`. */
export function Ficha() {
  const { id = '' } = useParams();
  return <FichaContenido id={id} />;
}

/** La ficha en sí, sin saber de dónde le llega el id. Es un componente aparte porque se
 *  monta en dos sitios: en su página (`/pelicula/:id`) y dentro de un diálogo sobre la
 *  watchlist (`DialogoFicha`), donde navegar a otra pantalla tiraba la página, el filtro y
 *  el término de búsqueda de la lista al volver. */
export function FichaContenido({ id }: { id: string }) {
  const qc = useQueryClient();
  const [mensaje, setMensaje] = useState<{ tipo: 'success' | 'error' | 'info' | 'warning'; texto: string } | null>(null);
  // Qué versión de una serie está desplegada, por su `elinkId`. Una a la vez: cada una
  // trae decenas de filas y dos abiertas convierten la tabla en un rollo.
  const [desplegada, setDesplegada] = useState<number | null>(null);

  const { data, isLoading, error } = useQuery<FichaVista>({
    queryKey: ['ficha', id],
    queryFn: () => api.ficha(id),
  });

  // Las preferencias son las mismas de Ajustes. Si no llegan —API vieja, un fallo puntual—
  // se colorea con las de partida en vez de dejar la tabla en blanco: pintar de menos es
  // mejor que no pintar la ficha.
  const { data: ajustes } = useQuery({ queryKey: ['ajustes'], queryFn: api.ajustes });
  const prefs = ajustes?.settings.preferenciasVersion ?? PREFERENCIAS_VERSION_POR_DEFECTO;

  const encolar = useMutation({
    mutationFn: (releaseFileId: string) => api.encolar(releaseFileId),
    onSuccess: (r) => {
      setMensaje({ tipo: 'success', texto: `Encolada: ${r.download.fileName}` });
      void qc.invalidateQueries({ queryKey: ['descargas'] });
      // La ficha trae qué ficheros están ya pedidos: hay que releerla para que el botón
      // del episodio pase a decirlo.
      void qc.invalidateQueries({ queryKey: ['ficha', id] });
    },
    onError: (e) => {
      const f = e as ApiFallo;
      setMensaje({
        tipo: f.code === 'already_queued' ? 'info' : 'error',
        texto: explicarFallo(f),
      });
    },
  });

  /** Una temporada entera de un grupo (D-27): una descarga por episodio. */
  const encolarTemporada = useMutation({
    mutationFn: (v: { elinkId: number; season: number | null }) => api.encolarTemporada(v.elinkId, v.season),
    onSuccess: (r) => {
      setMensaje(resumirLote(r));
      void qc.invalidateQueries({ queryKey: ['descargas'] });
      void qc.invalidateQueries({ queryKey: ['ficha', id] });
    },
    onError: (e) => setMensaje({ tipo: 'error', texto: explicarFallo(e as ApiFallo) }),
  });

  const encolandoSerie: EncolandoSerie = encolarTemporada.isPending && encolarTemporada.variables
    ? { tipo: 'temporada', ...encolarTemporada.variables }
    : encolar.isPending && encolar.variables
      ? { tipo: 'episodio', fileId: encolar.variables }
      : null;

  if (isLoading) return <CircularProgress />;
  if (error) return <Alert severity="error">{(error as ApiFallo).message}</Alert>;
  if (!data) return null;

  const esSerie = data.kind === 'show';
  const yaLaTienes = data.library.status === 'owned' && !data.library.upgradeAvailable;
  // La duración y el país son de la ficha, no de cada versión: la primera convierte un
  // tamaño en un bitrate —lo único comparable entre películas que duran distinto— y el
  // segundo dice qué voz es la original.
  const releases = ordenar(
    valorarVersiones(data.releases, prefs, { duracionMin: data.runtimeMin, paises: data.countries }),
  );

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
        {data.posterUrl && (
          <Box
            component="img"
            src={data.posterUrl.startsWith('http') ? data.posterUrl : `https://www.hispashare.org/${data.posterUrl}`}
            alt=""
            sx={{ width: 200, borderRadius: 2, alignSelf: 'flex-start' }}
          />
        )}
        <Stack spacing={1.5} sx={{ flex: 1 }}>
          <Box>
            <Typography variant="h4">
              {data.title} {data.year && <Typography component="span" variant="h5" color="text.secondary">({data.year})</Typography>}
            </Typography>
            {data.originalTitle && data.originalTitle !== data.title && (
              <Typography variant="body2" color="text.secondary">{data.originalTitle}</Typography>
            )}
          </Box>

          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
            {/* Serie o película, delante de todo (D-27): es lo que cambia cómo se lee el
                resto de la ficha, y el buscador las mezcla si se llega por otro camino. */}
            {esSerie && <Chip size="small" color="primary" label="Serie" />}
            <InsigniaBiblioteca estado={data.library} />
            {data.imdbRating && <Chip size="small" variant="outlined" label={`IMDb ${data.imdbRating}`} />}
            {data.runtimeMin && (
              <Chip size="small" variant="outlined" label={`${data.runtimeMin} min${esSerie ? ' por episodio' : ''}`} />
            )}
            {data.genres.slice(0, 4).map((g) => <Chip key={g} size="small" variant="outlined" label={g} />)}
          </Stack>

          {data.director && <Typography variant="body2"><b>Dirección:</b> {data.director}</Typography>}
          {data.synopsis && <Typography variant="body2" color="text.secondary">{data.synopsis}</Typography>}
        </Stack>
      </Stack>

      {mensaje && <Alert severity={mensaje.tipo} onClose={() => setMensaje(null)}>{mensaje.texto}</Alert>}

      <Box>
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap' }}
        >
          <Typography variant="h6" gutterBottom>
            Versiones disponibles ({releases.length})
          </Typography>
          {/* Un color sin regla a la vista es un adorno. La regla se edita en Ajustes y se
              llega desde aquí, que es donde se descubre que no cuadra con lo que se quiere. */}
          <Typography variant="caption" color="text.secondary">
            ✓ lo que quieres · ≈ te vale · ✗ no te vale · ? el sitio no lo dice ·{' '}
            <Box component={RouterLink} to="/ajustes" sx={{ color: 'primary.main' }}>
              cambiar preferencias
            </Box>
          </Typography>
        </Stack>

        {releases.length === 0 ? (
          <Alert severity="info">Esta ficha no ofrece ningún enlace.</Alert>
        ) : (
          <TableContainer component={Paper}>
            {/* Cada característica en su columna y con su color: las versiones de un
                título se distinguen por letra pequeña —`mkv (x265-EAC3)` frente a
                `avi (XviD-AC3)`— y leerla en un renglón corrido obliga a compararlas de
                memoria. El motivo de cada color va en el tooltip de su celda. */}
            <Table size="small" sx={{ minWidth: 1080 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Resolución</TableCell>
                  <TableCell>Formato</TableCell>
                  <TableCell>Vídeo</TableCell>
                  <TableCell>Audio</TableCell>
                  <TableCell>Subtítulos</TableCell>
                  <TableCell>Tamaño</TableCell>
                  <TableCell align="right">Descargas</TableCell>
                  <TableCell>Contenido</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {releases.map(({ version: r, valoracion: v }) => {
                  const video = r.files.find((f) => f.kind === 'video');
                  const noMejora = yaLaTienes && !esSerie;
                  // `isPending` a secas apagaba los botones de TODOS los releases: se
                  // pulsaba uno y la tabla entera se quedaba en gris. Solo espera el que
                  // se ha pulsado, que es el único que tiene algo en marcha.
                  const encolando = encolar.isPending && encolar.variables === video?.id;
                  const detalle = esSerie ? describirSerie(r) : describirFicheros(r);
                  const abierta = esSerie && desplegada === r.elinkId;
                  return (
                    <Fragment key={r.elinkId}>
                    <TableRow sx={{ opacity: noMejora ? 0.55 : 1 }}>
                      <CeldaIdoneidad
                        juicio={v.resolucion}
                        valor={r.resolution ?? 'Sin determinar'}
                        detalle={r.qualityRaw}
                      />
                      <CeldaIdoneidad
                        juicio={v.contenedor}
                        valor={r.container?.toUpperCase() ?? '—'}
                      />
                      <CeldaIdoneidad
                        juicio={v.codec}
                        // El texto crudo del sitio, no la familia: `x265 10Bit Dolby Vision
                        // HDR` dice más que «H.265», y lo que se compara ya lo explica el
                        // tooltip.
                        valor={
                          <Box sx={{ maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {r.videoCodec ?? '—'}
                          </Box>
                        }
                        detalle={r.audioCodec}
                      />
                      <CeldaIdoneidad
                        juicio={v.audio}
                        valor={
                          r.audioLangs.length ? (
                            <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                              {r.audioLangs.map((l) => <Chip key={l} size="small" label={l.toUpperCase()} />)}
                            </Stack>
                          ) : (
                            '—'
                          )
                        }
                      />
                      {/* Se juzgan con la misma vara que el audio, pero sin llegar nunca a
                          rojo: unos subtítulos que faltan se descargan aparte, así que no
                          descartan una versión que por lo demás está bien.

                          Los forzados se pintan pero NO cuentan: solo traducen lo que en la
                          propia película ya suena en otro idioma, y contarlos prometería una
                          subtitulación que no existe. */}
                      <CeldaIdoneidad
                        juicio={v.subtitulos}
                        valor={
                          <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                            {r.subtitleLangs.map((l) => (
                              <Chip key={`s-${l}`} size="small" variant="outlined" label={l.toUpperCase()} />
                            ))}
                            {r.forcedSubLangs.map((l) => (
                              <Tooltip
                                key={`f-${l}`}
                                title="Subtítulos forzados: solo traducen los diálogos que ya venían en otro idioma, no la película entera"
                              >
                                <Chip size="small" variant="outlined" label={`forz. ${l.toUpperCase()}`} />
                              </Tooltip>
                            ))}
                            {r.subsSinEspecificar && <Chip size="small" variant="outlined" label="subs" />}
                            {!r.subtitleLangs.length && !r.forcedSubLangs.length && !r.subsSinEspecificar && '—'}
                          </Stack>
                        }
                      />
                      <CeldaIdoneidad
                        juicio={v.tamano}
                        valor={formatearTamano(r.sizeBytes)}
                        // El bitrate es LO que se juzga, así que se enseña: es el número
                        // que explica por qué dos versiones del mismo tamaño no valen lo
                        // mismo cuando una película dura el doble que otra.
                        detalle={(() => {
                          const mbps = bitrateMbps(r, data.runtimeMin);
                          const videos = r.files.filter((f) => f.kind === 'video').length;
                          if (mbps == null) return null;
                          return `${mbps.toFixed(1).replace('.', ',')} Mbps${videos > 1 ? ` · ${videos} vídeos` : ''}`;
                        })()}
                      />
                      <TableCell align="right">{r.downloadCount ?? '—'}</TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {detalle ?? video?.fileName ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {esSerie ? (
                          // Una serie no se descarga de un botón: se despliega y se elige
                          // temporada o episodio (D-27).
                          <Button
                            size="small"
                            variant={abierta ? 'outlined' : v.conjunto === 'rechazada' ? 'outlined' : 'contained'}
                            onClick={() => setDesplegada(abierta ? null : r.elinkId)}
                            aria-expanded={abierta}
                          >
                            {abierta ? 'Ocultar' : 'Episodios'}
                          </Button>
                        ) : (
                          <Tooltip
                            title={
                              video?.descarga
                                ? 'Este fichero ya está en Descargas'
                                : noMejora ? 'Ya tienes esta película en igual o mejor calidad' : ''
                            }
                          >
                            <span>
                              <Button
                                size="small"
                                variant={noMejora || v.conjunto === 'rechazada' || video?.descarga ? 'outlined' : 'contained'}
                                disabled={!video || encolando || !!video.descarga}
                                onClick={() => {
                                  // El id del fichero lo asigna el backend al guardar la ficha;
                                  // aquí se manda la posición dentro del release.
                                  const f = r.files.find((x) => x.kind === 'video');
                                  if (f?.id) encolar.mutate(f.id);
                                }}
                              >
                                {video?.descarga ? 'Ya en descargas' : encolando ? 'Encolando…' : 'Descargar'}
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                    {abierta && (
                      <TableRow>
                        <TableCell colSpan={9} sx={{ p: 0 }}>
                          <TemporadasDeVersion
                            version={r}
                            cruceInseguro={data.library.confidence === 'fuzzy'}
                            encolando={encolandoSerie}
                            onEpisodio={(fileId) => encolar.mutate(fileId)}
                            onTemporada={(season) => encolarTemporada.mutate({ elinkId: r.elinkId, season })}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Stack>
  );
}
